// Engineer working-location session persistence. The engineer's active
// postcode + coordinates live in sessionStorage (per browser tab) and drive
// every engineer page's location context. Centralised here so the postcode
// screen and the Change Location drawer write it identically.

import { normalizePostcode } from './postcodes'
import type { BranchAtPosition } from './api'
import type { PostcodeCoordinates } from './postcodes'

export const ENGINEER_SESSION_KEY = 'engineerSession'
export const POSTCODE_JUST_SET_KEY = 'postcodeJustSet'

/**
 * How the counter was found.
 *
 * `device` means the phone reported a position near it, so the engineer is
 * standing there. `postcode` means it is merely near the postcode they typed,
 * which says nothing about where they are — the wording everywhere downstream
 * has to stay honest about the difference.
 */
export type AtBranchSource = 'device' | 'postcode'

/**
 * The trade counter attached to this location (DEV-51045).
 *
 * Held in the session, not on the device or the server: "saving a branch
 * beyond the session" is out of scope, so it dies with the tab on its own.
 * The whole branch is stored rather than its id alone, so the search page
 * can render a pill for it without a second lookup.
 */
export interface EngineerSessionAtBranch {
  branchId: string
  branchName: string
  supplierId: string
  supplierName: string
  source: AtBranchSource
}

export interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
  /**
   * Every branch the engineer chose to show first from this location, nearest
   * first. Empty when they chose none. Several can be chosen, since "Show
   * first" already takes more than one tick.
   */
  atBranches: EngineerSessionAtBranch[]
}

/** Stored shape, including the single `atBranch` written by earlier builds. */
type StoredEngineerSession = Partial<EngineerSession> & {
  atBranch?: Partial<EngineerSessionAtBranch> | null
  atBranches?: Array<Partial<EngineerSessionAtBranch>> | null
}

/** Validate one stored branch, or null if it is unusable. */
function parseStoredAtBranch(
  value: Partial<EngineerSessionAtBranch> | null | undefined,
): EngineerSessionAtBranch | null {
  if (
    !value ||
    typeof value.branchId !== 'string' ||
    typeof value.supplierId !== 'string'
  ) {
    return null
  }
  return {
    branchId: value.branchId,
    branchName: String(value.branchName ?? ''),
    supplierId: value.supplierId,
    supplierName: String(value.supplierName ?? ''),
    // Written before the typed-postcode route existed, so from the device.
    source: value.source === 'postcode' ? 'postcode' : 'device',
  }
}

/** The postcode in use and the nearby branches currently pinned for it. */
export interface CurrentAtBranches {
  postcode: string
  branchIds: string[]
  branches: EngineerSessionAtBranch[]
}

/**
 * Which of the offered nearby branches start ticked.
 *
 * Whatever is ticked when the engineer confirms becomes their nearby pins,
 * exactly. So for the postcode they're already on, the card has to open on
 * what's pinned now — otherwise confirming without touching anything would
 * wipe it. For any other postcode, or when nothing is pinned yet: a lone
 * branch starts ticked (AC1), several start unticked.
 */
export function initialAtBranchSelection(
  postcode: string,
  branches: BranchAtPosition[],
  current: CurrentAtBranches | null,
): string[] {
  const isCurrentPostcode =
    current !== null &&
    normalizePostcode(postcode.trim()) === normalizePostcode(current.postcode)
  if (isCurrentPostcode && current.branchIds.length > 0) {
    const pinned = new Set(current.branchIds)
    return branches
      .filter((branch) => pinned.has(branch.branchId))
      .map((branch) => branch.branchId)
  }
  return branches.length === 1 ? [branches[0].branchId] : []
}

/**
 * The nearby branches to save when the engineer confirms a postcode.
 *
 * The card only decides for the branches it actually showed: those ticked
 * are pinned, those unticked are not. For the postcode already in use, any
 * pin the card didn't show is kept as it is — the branches may still have
 * been loading, the lookup may have failed, or the pin may have come from the
 * device and sit outside this postcode's list. Reading "not shown" as "un-
 * ticked" would silently wipe pins the engineer never saw. For any other
 * postcode, the previous location's pins don't carry over.
 */
export function resolveAtBranchesToSave(
  postcode: string,
  offered: BranchAtPosition[],
  selectedIds: string[],
  source: AtBranchSource,
  current: CurrentAtBranches | null,
): EngineerSessionAtBranch[] {
  const isCurrentPostcode =
    current !== null &&
    normalizePostcode(postcode.trim()) === normalizePostcode(current.postcode)
  // A branch already picked from the device keeps saying "where you are"
  // when the same postcode is merely re-confirmed by typing: the engineer
  // hasn't moved, and downgrading it would turn a no-change into a save.
  const pickedFromDevice = new Set(
    isCurrentPostcode
      ? current.branches
          .filter((branch) => branch.source === 'device')
          .map((branch) => branch.branchId)
      : [],
  )
  const ticked = offered
    .filter((branch) => selectedIds.includes(branch.branchId))
    .map((branch) =>
      toSessionAtBranch(
        branch,
        pickedFromDevice.has(branch.branchId) ? 'device' : source,
      ),
    )
  if (!isCurrentPostcode) return ticked
  const shownIds = new Set(offered.map((branch) => branch.branchId))
  const keptUnshown = current.branches.filter(
    (branch) => !shownIds.has(branch.branchId),
  )
  // Nearest-first ticked branches lead, so the pills on search do too.
  return [...ticked, ...keptUnshown]
}

/**
 * Whether two branch lists pick the same branches, found the same way.
 *
 * The source counts because it drives the wording on search ("where you are"
 * vs "near your postcode"). Order doesn't: it only follows whichever lookup
 * ran last, and counting it would turn an untouched confirm into a save.
 */
export function isSameBranchSelection(
  a: EngineerSessionAtBranch[],
  b: EngineerSessionAtBranch[],
): boolean {
  const sourceById = new Map(b.map((branch) => [branch.branchId, branch.source]))
  return (
    new Set(a.map((branch) => branch.branchId)).size === sourceById.size &&
    a.every((branch) => sourceById.get(branch.branchId) === branch.source)
  )
}

/** Narrow a looked-up branch to what the session keeps, tagged with its source. */
export function toSessionAtBranch(
  branch: BranchAtPosition,
  source: AtBranchSource,
): EngineerSessionAtBranch {
  return {
    branchId: branch.branchId,
    branchName: branch.branchName,
    supplierId: branch.supplierId,
    supplierName: branch.supplierName,
    source,
  }
}

// Screens and the "Show first" row must see a location change at once, so
// reads go through one store and every write notifies it. sessionStorage
// fires no event in the tab that wrote it, hence the explicit listeners.
const listeners = new Set<() => void>()

export function subscribeToEngineerSession(listener: () => void): () => void {
  listeners.add(listener)
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

/**
 * The raw stored string, so a `useSyncExternalStore` snapshot stays
 * referentially stable and parsing can happen in a memo.
 */
export function readEngineerSessionRaw(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return sessionStorage.getItem(ENGINEER_SESSION_KEY)
  } catch {
    return null
  }
}

/** Parse a stored session. Returns null for anything unusable. */
export function parseEngineerSession(raw: string | null): EngineerSession | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const record = parsed as StoredEngineerSession
    if (typeof record.postcode !== 'string' || !record.postcode.trim()) return null
    if (typeof record.latitude !== 'number' || typeof record.longitude !== 'number') {
      return null
    }
    // A session written by an earlier build holds a single `atBranch`; read it
    // as a list of one so a tab left open across a deploy keeps its pill.
    const storedBranches = Array.isArray(record.atBranches)
      ? record.atBranches
      : [record.atBranch]
    return {
      postcode: record.postcode,
      latitude: record.latitude,
      longitude: record.longitude,
      atBranches: storedBranches
        .map(parseStoredAtBranch)
        .filter((branch): branch is EngineerSessionAtBranch => branch !== null),
    }
  } catch {
    return null
  }
}

/**
 * Persist the engineer's working location to sessionStorage.
 *
 * Also stamps a short-lived "just set" marker that `usePostcodeSync` uses to
 * skip its cross-device mismatch check for a few seconds — long enough for the
 * freshly-written session and the `auth-me` cache to line up, so the user is
 * not bounced back to the postcode screen right after setting it.
 *
 * Lives in a plain module (not a React component/hook) so the `Date.now()`
 * timestamp is outside the render path.
 */
export function writeEngineerSession(
  postcode: string,
  coordinates: PostcodeCoordinates,
  atBranches: EngineerSessionAtBranch[] = [],
): void {
  const session: EngineerSession = {
    postcode,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    atBranches,
  }
  sessionStorage.setItem(ENGINEER_SESSION_KEY, JSON.stringify(session))
  sessionStorage.setItem(POSTCODE_JUST_SET_KEY, Date.now().toString())
  for (const listener of listeners) listener()
}
